<template>
  <PageWrapper :isLoading="isLoading" hasFilters @submitFilters="applyFilters">
    <template #title> لیست تمام نظرات </template>

    <template #titleBtn>
      <BButton variant="success" @click="openCreateModal">
        ایجاد نظر
      </BButton>
    </template>

    <template #filters>
      <div class="filters-width w-24">
        <BFormGroup label="وضعیت نمایش" label-class="">
          <BFormSelect v-model="ui.confirmed" :options="confirmedOptions" />
        </BFormGroup>
      </div>

      <div class="filters-width w-24">
        <BFormGroup label="نوع نظر" label-class="">
          <BFormSelect v-model="ui.type" :options="typeOptions" />
        </BFormGroup>
      </div>

      <div class="filters-width w-24" style="margin-top: 11px;">
        <BFormGroup class="mb-25">
          <div v-if="ui.objectId && ui.type">
            <BFormSelect
                v-model="ui.objectId"
                :options="selectedObjectSelectOptions"
                @change="onSelectedObjectSelectChange"
            />
          </div>

          <div v-else>
            <div v-if="Number(ui.type) === 5">
              <MasterPicker @getMaster="setSelectedMaster" />
            </div>

            <div v-else-if="Number(ui.type) === 3">
              <LessonPicker closeOnSelect @getLesson="setSelectedLesson" />
            </div>

            <div v-else-if="Number(ui.type) === 6">
              <LessonCategoryPicker closeOnSelect @getLessonCategory="setSelectedPackageCategory" />
            </div>
          </div>
        </BFormGroup>
      </div>

      <div class="filters-width w-24">
        <BFormGroup label="جستجو در متن نظر" label-class="mb-25">
          <BFormInput
              v-model="ui.searchComFermand"
              placeholder="متن کامنت خود را بنویسید"
              @keyup.enter="applyFilters"
          />
        </BFormGroup>
      </div>
    </template>

    <BCard>
      <CustomTable
          :pageNumber="list.pageNumber"
          :count="list.count"
          :totalCount="totalCount"
          :tableColumns="myTableColumns"
          :tableItems="tableItems"
          @changePage="changePage"
      >
        <template #select="data">
          <BFormCheckbox
              :checked="selectedIds.includes(data.item.commentId)"
              @change="toggleSelect(data.item.commentId, $event)"
          />
        </template>

        <template #commentId="data">
          {{ data.item.commentId }}
        </template>

        <template #commentType="data">
          {{ getTypeLabelUI(data.item.type) }}
        </template>

        <template #userFullName="data">
          {{ getUserFullName(data.item) }}
        </template>

        <template #product="data">
          {{ getProductDisplay(data.item) }}
        </template>

        <template #content="data">
          <div style="max-width: 520px">
            <div v-if="hasMedia(data.item)" class="text-muted" style="font-size: 12px">
               (در مشاهده نمایش داده می‌شود)
            </div>

            <div v-if="String(data.item.message || '').trim()">
              {{ truncateText(data.item.message, 120) }}
            </div>

            <div v-else-if="!hasMedia(data.item)" class="text-muted" style="font-size: 12px">
              بدون متن
            </div>
          </div>
        </template>

        <template #createDate="data">
          {{ formatDateTimeNoSeconds(data.item.createDate) }}
        </template>

        <template #isConfirmed="data">
          <BFormCheckbox
              switch
              :checked="!!data.item.isConfirmed"
              @change="toggleConfirm(data.item, $event)"
          />
        </template>

        <template #actions="data">
          <div class="d-flex align-items-center justify-content-center" style="gap: 10px">
            <BButton
                variant="transparent"
                size="sm"
                class="icon-btn"
                @click="openViewModal(data.item)"
                title="مشاهده"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </BButton>

            <BButton
                variant="transparent"
                size="sm"
                class="icon-btn text-primary"
                @click="openEditModal(data.item)"
                title="ویرایش"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </BButton>

            <BButton
                variant="transparent"
                size="sm"
                class="icon-btn text-danger"
                @click="deleteComment(data.item)"
                title="حذف"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </BButton>
          </div>
        </template>
      </CustomTable>
    </BCard>

    <CommentViewModal v-model="showViewModal" :comment="selectedComment" @hidden="selectedComment = null" />

    <CommentCreateEditModal
        :value="showCreateModal"
        @input="showCreateModal = $event"
        :editing-item="editingItem"
        :comment-type-options="commentTypeOptions"
        :media-type-options-u-i="mediaTypeOptionsUI"
        @submit="submitComment"
        @hidden="onCreateModalHidden"
        @refresh-list="refreshList"
    />
  </PageWrapper>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue"
import { GetAll, CreateComment, UpdateComment, DeleteBy } from "@/libs/Api/Comments"
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormCheckbox,
  BFormSelect,
  BBadge,
  BFormInput,
  BFormGroup,
} from "bootstrap-vue"

import PageWrapper from "@/views/components/Utils/PageWrapper.vue"
import CustomTable from "@/views/components/Utils/CustomTable.vue"

import CommentViewModal from "@/@core/components/comments/ViewModal.vue"
import CommentCreateEditModal from "@/@core/components/comments/CreateEditModal.vue"

import MasterPicker from "@/views/components/MasterActiveCourse/MasterPicker.vue"
import LessonPicker from "@/views/components/Lessons/LessonPicker.vue"
import LessonCategoryPicker from "@/views/components/LessonCategories/LessonCategoryPicker.vue"

import {
  formatDateTimeNoSeconds,
  truncateText,
  getTypeLabel,
  getMediaLabel,
  getUserFullName,
  getProductDisplay,
  hasMedia,
} from "@/@core/utils/commentUtils"

export default {
  name: "CommentsIndex",
  components: {
    PageWrapper,
    CustomTable,
    BRow,
    BCol,
    BCard,
    BButton,
    BFormCheckbox,
    BFormSelect,
    BBadge,
    BFormInput,
    BFormGroup,
    CommentViewModal,
    CommentCreateEditModal,
    MasterPicker,
    LessonPicker,
    LessonCategoryPicker,
  },

  mounted() {
    this.applyFilters()
  },

  data() {
    return {
      isLoading: false,
      list: { pageNumber: 1, count: 10 },
      totalCount: 0,
      commentsRaw: [],
      selectedIds: [],

      ui: {
        confirmed: null,
        type: null,
        objectId: "",
        searchComFermand: "",
      },

      applied: {
        confirmed: null,
        type: null,
        objectId: null,
        searchComFermand: "",
      },

      selectedTitles: { master: "", lesson: "", pkg: "" },
      appliedTitles: { master: "", lesson: "", pkg: "" },

      showViewModal: false,
      selectedComment: null,

      showCreateModal: false,
      editingItem: null,

      confirmedOptions: [
        { text: "همه", value: null },
        { text: "تایید شده", value: true },
        { text: "تایید نشده", value: false },
      ],

      typeOptions: [
        { text: "همه", value: null },
        { text: "استاد", value: 5 },
        { text: "درس", value: 3 },
        { text: "پکیج درسی", value: 6 },
      ],

      commentTypeOptions: [
        { name: "استاد", value: 5 },
        { name: "درس", value: 3 },
        { name: "پکیج درسی", value: 6 },
      ],

      mediaTypeOptionsUI: [
        { name: "تصویر", value: 2 },
        { name: "ویدیو", value: 4 },
        { name: "صوت", value: 3 },
      ],

      myTableColumns: [
        { key: "commentId", label: "شناسه" },
        { key: "commentType", label: "نوع نظر" },
        { key: "userFullName", label: "نام و نام خانوادگی" },
        { key: "product", label: "محصول" },
        { key: "content", label: "پیام" },
        { key: "createDate", label: "تاریخ ثبت" },
        { key: "isConfirmed", label: "وضعیت نمایش در سایت" },
        { key: "actions", label: "عملیات" },
      ],
    }
  },

  computed: {
    tableItems() {
      return Array.isArray(this.commentsRaw) ? this.commentsRaw : []
    },

    selectedObjectLabel() {
      const t = Number(this.ui.type)
      const id = Number(this.ui.objectId || 0)
      if (!t || !id) return ""
      if (t === 5) return this.selectedTitles.master || `#${id}`
      if (t === 3) return this.selectedTitles.lesson || `#${id}`
      if (t === 6) return this.selectedTitles.pkg || `#${id}`
      return `#${id}`
    },

    appliedObjectLabel() {
      const t = Number(this.applied.type)
      const id = Number(this.applied.objectId || 0)
      if (!t || !id) return ""
      if (t === 5) return this.appliedTitles.master || `#${id}`
      if (t === 3) return this.appliedTitles.lesson || `#${id}`
      if (t === 6) return this.appliedTitles.pkg || `#${id}`
      return `#${id}`
    },

    selectedObjectSelectOptions() {
      const val = String(this.ui.objectId || "")
      const label = this.selectedObjectLabel || (val ? `#${val}` : "انتخاب کنید")
      return [
        { text: "انتخاب کنید", value: "" },
        { text: label, value: val },
      ]
    },

    appliedBadgeText() {
      const parts = []
      const f = this.applied
      if (f.searchComFermand) parts.push(`جستجو: ${f.searchComFermand}`)
      if (f.confirmed !== null) parts.push(f.confirmed ? "تایید شده" : "تایید نشده")
      if (Number(f.type)) parts.push(`نوع: ${this.getTypeLabelUI(f.type)}`)
      if (Number(f.objectId) && Number(f.type)) parts.push(`انتخاب: ${this.appliedObjectLabel}`)
      return parts.join(" | ")
    },
  },

  watch: {
    "ui.type"() {
      this.clearObjectSelection()
    },
  },

  methods: {
    formatDateTimeNoSeconds,
    truncateText,
    getMediaLabel,
    getUserFullName,
    getProductDisplay(item) {
      const type = Number(item.type)
      const objectId = Number(item.objectId || 0)

      if (!type || !objectId) return "-"

      let title = String(item.objectTitle || "").trim()

      if (!title) {
        if (type === 5) {
          title = String(item.masterFullName || item.masterName || "").trim()
        } else if (type === 3) {
          title = String(item.lessonTitle || item.title || "").trim()
        } else if (type === 6) {
          title = String(item.categoryTitle || item.title || "").trim()
        }
      }

      return title || `#${objectId}`
    },
    hasMedia,

    getTypeLabelUI(type) {
      const t = Number(type)
      if (t === 5) return "استاد"
      if (t === 3) return "درس"
      if (t === 6) return "پکیج درسی"
      try {
        return getTypeLabel(type)
      } catch {
        return String(type ?? "")
      }
    },

    onSelectedObjectSelectChange(v) {
      if (!String(v || "").trim()) this.clearObjectSelection()
    },

    clearObjectSelection() {
      this.ui.objectId = ""
      this.selectedTitles.master = ""
      this.selectedTitles.lesson = ""
      this.selectedTitles.pkg = ""
    },

    clearAppliedTitles() {
      this.appliedTitles.master = ""
      this.appliedTitles.lesson = ""
      this.appliedTitles.pkg = ""
    },

    setSelectedMaster(master) {
      const id = Number(master?.userId || master?.id || 0)
      this.ui.objectId = id ? String(id) : ""
      const title = String(master?.fullName || master?.name || master?.title || "").trim()
      this.selectedTitles.master = title || (id ? `#${id}` : "")
    },

    setSelectedLesson(payload) {
      let id = 0
      let title = ""

      if (typeof payload === "object" && payload !== null) {
        id = Number(payload?.lessonId || payload?.id || payload?.value || payload?.courseLessonId || 0)
        title = String(payload?.title || payload?.lessonTitle || payload?.name || payload?.label || "").trim()

        if (!title && payload.data) {
          title = String(payload.data.title || payload.data.lessonTitle || payload.data.name || "").trim()
        }
      } else if (typeof payload === "number") {
        id = Number(payload)
      }

      this.ui.objectId = id ? String(id) : ""
      this.selectedTitles.lesson = title || (id ? `#${id}` : "")
    },

    setSelectedPackageCategory(payload) {
      let id = 0
      let title = ""

      if (typeof payload === "object" && payload !== null) {
        id = Number(payload?.id || payload?.value || payload?.lessonCategoryId || payload?.categoryId || 0)
        title = String(payload?.title || payload?.categoryTitle || payload?.name || payload?.label || "").trim()

        if (!title && payload.data) {
          title = String(payload.data.title || payload.data.categoryTitle || payload.data.name || "").trim()
        }
      } else if (typeof payload === "number") {
        id = Number(payload)
      }

      this.ui.objectId = id ? String(id) : ""
      this.selectedTitles.pkg = title || (id ? `#${id}` : "")
    },

    normalizeOk(res) {
      if (res?.isSuccess === true) return true
      if (res?.data?.isSuccess === true) return true
      if (res?.status === 200 || res?.status === 201) return true
      if (res?.statusCode === 200 || res?.statusCode === 201) return true
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) return true
      return false
    },

    unwrap(res) {
      if (!res) return res
      if (res?.data && (typeof res?.status === "number" || res?.headers)) return res.data
      return res
    },

    extractItems(res) {
      const root = this.unwrap(res)
      if (Array.isArray(root?.data?.data)) return root.data.data
      if (Array.isArray(root?.data)) return root.data
      if (Array.isArray(root)) return root
      return []
    },

    extractTotal(res) {
      const root = this.unwrap(res)
      const n = Number(root?.data?.total ?? root?.total ?? null)
      return Number.isFinite(n) && n >= 0 ? n : null
    },

    applyFilters() {
      const type = this.ui.type ? Number(this.ui.type) : null
      const objectId = Number(this.ui.objectId) ? Number(this.ui.objectId) : null

      this.applied = {
        confirmed: this.ui.confirmed,
        type,
        objectId: type && objectId ? objectId : null,
        searchComFermand: String(this.ui.searchComFermand || "").trim(),
      }

      this.clearAppliedTitles()
      if (this.applied.type === 5) this.appliedTitles.master = this.selectedTitles.master
      if (this.applied.type === 3) this.appliedTitles.lesson = this.selectedTitles.lesson
      if (this.applied.type === 6) this.appliedTitles.pkg = this.selectedTitles.pkg

      this.list.pageNumber = 1
      this.selectedIds = []
      this.getAllComments()
    },

    clearFilters() {
      this.ui = { confirmed: null, type: null, objectId: "", searchComFermand: "" }
      this.clearObjectSelection()
      this.clearAppliedTitles()
      this.applyFilters()
    },

    buildQueryParams() {
      const params = {
        pageNumber: Number(this.list.pageNumber) || 1,
        count: Number(this.list.count) || 10,
      }

      if (this.applied.searchComFermand) params.searchComFermand = this.applied.searchComFermand
      if (this.applied.confirmed !== null) params.confirmed = this.applied.confirmed
      if (Number(this.applied.type)) params.type = Number(this.applied.type)
      if (Number(this.applied.objectId) && Number(this.applied.type)) params.Id = Number(this.applied.objectId)

      return params
    },

    changePage(pageNumber) {
      this.list.pageNumber = Number(pageNumber) || 1
      this.selectedIds = []
      this.getAllComments()
    },

    toggleSelect(id, checked) {
      const ids = new Set(this.selectedIds || [])
      if (checked) ids.add(id)
      else ids.delete(id)
      this.selectedIds = Array.from(ids)
    },

    selectAllRows() {
      const current = Array.isArray(this.tableItems) ? this.tableItems : []
      if (!current.length) return
      if (this.selectedIds.length < current.length) {
        this.selectedIds = Array.from(new Set(current.map((x) => x.commentId)))
      } else {
        this.selectedIds = []
      }
    },

    async getAllComments() {
      try {
        this.isLoading = true
        const req = new GetAll(this)
        req.setRequestParam(this.buildQueryParams())

        await req.fetch((res) => {
          const items = this.extractItems(res)
          const total = this.extractTotal(res)
          items.forEach(item => {
            if (item.type === 5 && (item.masterFullName)) {
              item.objectTitle = item.masterFullName
            } else if (item.type === 3 && (item.title)) {
              item.objectTitle = item.title
            } else if (item.type === 6 && ( item.title)) {
              item.objectTitle = item.title
            }
          })

          this.commentsRaw = items.slice()
          this.totalCount = total === null ? items.length : total
        })
      } catch (e) {
        console.error(e)
        this.$toast({
          component: ToastificationContent,
          props: { title: "خطا در دریافت لیست نظرات", icon: "AlertTriangleIcon", variant: "danger" },
        })
      } finally {
        this.isLoading = false
      }
    },

    refreshList() {
      this.getAllComments()
    },

    openViewModal(item) {
      this.selectedComment = item || null
      this.showViewModal = true
    },

    openCreateModal() {
      this.editingItem = null
      this.showCreateModal = true
    },

    openEditModal(item) {
      this.editingItem = item || null
      this.showCreateModal = true
    },

    onCreateModalHidden() {
      if (!this.showCreateModal) this.editingItem = null
    },

    applyUpdatedRowLocally(updated) {
      const id = Number(updated?.commentId || 0)
      if (!id) return
      const idx = this.commentsRaw.findIndex((x) => Number(x.commentId) === id)
      if (idx === -1) return
      this.commentsRaw.splice(idx, 1, Object.assign({}, this.commentsRaw[idx], updated))
    },

    async submitComment(payload) {
      try {
        this.isLoading = true
        const isEditing = Number(payload?.commentId || 0) > 0

        if (isEditing) {
          const req = new UpdateComment(this)
          req.setRequestParamDataObj(payload)

          await req.fetch((res) => {
            if (!this.normalizeOk(res)) {
              this.$toast({
                component: ToastificationContent,
                props: { title: "ویرایش انجام نشد", icon: "XIcon", variant: "danger" },
              })
              return
            }

            this.$toast({
              component: ToastificationContent,
              props: { title: "نظر با موفقیت ویرایش شد", icon: "CheckcIcon", variant: "success" },
            })

            this.showCreateModal = false
            this.editingItem = null
            this.getAllComments()
          })
          return
        }

        const req = new CreateComment(this)
        req.setRequestParamDataObj(payload)

        await req.fetch(async (res) => {
          if (!this.normalizeOk(res)) {
            this.$toast({
              component: ToastificationContent,
              props: { title: "ایجاد انجام نشد", icon: "XIcon", variant: "danger" },
            })
            return
          }

          this.$toast({
            component: ToastificationContent,
            props: { title: "نظر با موفقیت ایجاد شد", icon: "CheckcIcon", variant: "success" },
          })

          this.list.pageNumber = 1
          this.selectedIds = []
          await this.getAllComments()

          this.showCreateModal = false
        })
      } catch (e) {
        console.error(e)
        this.$toast({
          component: ToastificationContent,
          props: { title: "خطا", icon: "AlertTriangleIcon", variant: "danger" },
        })
      } finally {
        this.isLoading = false
      }
    },

    async toggleConfirm(item, checked) {
      const old = !!item.isConfirmed
      const next = !!checked
      item.isConfirmed = next

      try {
        this.isLoading = true

        const payload = {
          commentId: Number(item.commentId || 0),
          parentId: Number(item.parentId || 0),
          firstName: String(item.firstName || ""),
          lastName: String(item.lastName || ""),
          imageUser: String(item.imageUser || ""),
          message: String(item.message || ""),
          objectId: Number(item.objectId || 0),
          type: Number(item.type || 0),
          mediaType: Number(item.mediaType || 1),
          mediaUrl: String(item.mediaUrl || ""),
          userId: Number(item.userId || 0),
          createDate: item.createDate || new Date().toISOString(),
          isConfirmed: next,
        }

        const req = new UpdateComment(this)
        req.setRequestParamDataObj(payload)

        await req.fetch((res) => {
          if (!this.normalizeOk(res)) {
            item.isConfirmed = old
            this.$toast({
              component: ToastificationContent,
              props: { title: "تغییر وضعیت انجام نشد", icon: "XIcon", variant: "danger" },
            })
          }
        })
      } catch (e) {
        item.isConfirmed = old
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },

    async deleteComment(item) {
      try {
        const id = Number(item?.commentId || 0)
        if (!id) return

        const ok = window.confirm("آیا از حذف این نظر مطمئن هستید؟")
        if (!ok) return

        this.isLoading = true

        const req = new DeleteBy(this)
        req.setRequestParam({ id })

        await req.fetch(async (res) => {
          if (!this.normalizeOk(res)) {
            this.$toast({
              component: ToastificationContent,
              props: { title: "حذف انجام نشد", icon: "XIcon", variant: "danger" },
            })
            return
          }

          this.$toast({
            component: ToastificationContent,
            props: { title: "نظر حذف شد", icon: "CheckcIcon", variant: "success" },
          })

          const maxPage = Math.max(1, Math.ceil(Math.max(0, this.totalCount - 1) / this.list.count))
          if (this.list.pageNumber > maxPage) this.list.pageNumber = maxPage

          this.selectedIds = this.selectedIds.filter((x) => Number(x) !== id)
          await this.getAllComments()
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.icon-btn {
  border: 0;
  padding: 4px 6px;
  line-height: 1;
}
.icon-btn:focus {
  box-shadow: none !important;
}
.mb-25 {
  margin-bottom: 0.25rem;
}
</style>